import React, { useState, useCallback, useEffect, useMemo } from 'react';
import VideoLayout, { TextOverlay, PanMethod } from '../../VideoPlayer/VideoLayout';
import { makeStyles } from '@material-ui/core';
import SessionTimeline from '../Timeline/SessionTimeline';
import VideoControls from '../../VideoPlayer/VideoControls';
import { fetchPost, fetchGet } from '../../Util/fetchAPI';
import { VideoOverlayObject, Video, Coordinate } from '../../VideoPlayer/VideoTypes';
import { TSession } from '../Sessions';
import LabelPlayerSidebar from './LabelPlayerSidebar';
import LabelHelpButton from './Buttons/LabelHelpButton';
import GoBackButton from './Buttons/GoBackButton';
import LabelCircularProgress from './LabelCircularProgress';
import LabelDebugInfoButton from './Buttons/LabelDebugInfoButton';
import { LabelTask, LineupEntry, timeSpanToSeconds } from '../../../services/sessionModels';
import labelTasksService, { labelTaskInput } from '../../../services/labelTasks';
import { TLabelTask, Cursor, PositionBuffer, TaskStatus, FrameRange } from './SessionLabelTask';
import { ClickInfo, MouseButton } from './useClickHandler';
import { projection } from '../../Util/calib';
import PlayerMarker from '../../VideoOverlayObjects/PlayerMarker';
import Trail from '../../VideoOverlayObjects/Trail';
import ToggleMenuItem from '../../VideoPlayer/Menu/ToggleMenuItem';
import ActionableMenuItem from '../../VideoPlayer/Menu/ActionableMenuItem';
import BackspaceIcon from '@material-ui/icons/Backspace';
import HistoryIcon from '@material-ui/icons/History';
import OverwriteIcon from '@material-ui/icons/Update';
import Menu from '../../VideoPlayer/Menu/Menu';
import AreYouSure from '../../Util/AreYouSure';
import { useHotkeys } from 'react-hotkeys-hook';
import useValidationFlags, { Flag } from './useValidationFlags';
import FlagTrails from '../../VideoOverlayObjects/FlagTrails';
import FlagMarkers from '../../VideoOverlayObjects/FlagMarkers';
import useSupervisorFunctions from './useSupervisorFunctions';
import { useSnackbar } from 'notistack';
import { saneRound } from '../../Util/functions';
import useLocalStorage from '../../Util/useLocalStorage';
import LegacyLabelAssistantToggleButton from './Buttons/LegacyLabelAssistantToggleButton';

const useStyles = makeStyles(() => ({
  flexContainer: {
    display: 'flex',
  },
  sidebar: {
    width: '320px',
    minWidth: '320px',
    height: '60vh',
    overflowY: 'scroll',
    paddingRight: '32px',
  },
  sidebarRow: {
    padding: '8px 0px',
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  legacyToggleButton: {
    marginTop: 4,
  },
  videoContainer: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '80%',
    height: '60vh',
  },
  videoControlsContainer: {
    marginTop: '12px',
    width: '320px',
    minWidth: '320px',
    height: '12vh',
    minHeight: '100px',
    backgroundColor: '#ccf1',
    border: '1px dashed #000',
  },
  timelineContainer: {
    marginTop: '12px',
    width: '80%',
    height: '12vh',
  },
  buttonsTitle: {
    marginTop: 12,
    fontSize: '12px',
    color: '#444',
  },
  warningText: {
    fontSize: '14px',
    color: '#c44',
    fontStyle: 'bold',
  },
  permissionButton: {
    backgroundColor: "#900", 
    color: '#fff', 
    fontSize: '8px',
    margin: '4px 0',
  },
  overlay: {
    position: 'fixed',
    left: 0,
    right: 0,
    bottom: 0,
    top: 0,
    zIndex: 1000,
    backgroundColor: '#0009',
  },
  popup: {
    position: "relative",
    margin: '170px auto',
    padding: '20px',
    width: '30%',
    height: '400px',
    background: '#ffffff',
    border: '5px solid #ff8800',
    borderRadius: '12px',
    transition: 'all 5s ease-in-out',
  },
}));

type ClickAction = {
  viewId: string;
  info: ClickInfo;
};

type CursorPosition = {
  viewId: string;
  coord: Coordinate;
};

type PositionData = {
  frame_id: number;
  x: number;
  y: number;
};

type ClickPositionData = {
  id: number;
  frameId: number;
  viewCreated?: string;
  position?: Coordinate;
  video_coordinates?: Map<string, Coordinate>;
};

type Props = {
  session: TSession;
  task: TLabelTask;
  runId: string;
  lineup: LineupEntry[];
  labelTasks: LabelTask[];
  reloadTask: () => void;
};

export enum UserState {
  Init,
  Review,
  Verify,
  PlayUntilNewPosition,
  SetNewPosition,
  AddingPositions,
  Backtrack,
  Overwrite,
}

enum SubState {
  Entry,
  Active,
  Exit,
}

const isAddPositionState = (state: UserState) => {
  return (
    state === UserState.PlayUntilNewPosition ||
    state === UserState.SetNewPosition ||
    state === UserState.AddingPositions
  );
};

const getUserStateColor = (state: UserState) => {
  let color: string = '#fff';
  switch (state) {
    case UserState.Review:
      color = '#0099ff';
      break;
    case UserState.Verify:
      color = '#48c948';
      break;
    case UserState.PlayUntilNewPosition:
      color = '#e5ff00';
      break;
    case UserState.SetNewPosition:
      color = '#b91515';
      break;
    case UserState.AddingPositions:
      color = '#ff5a0f';
      break;
    case UserState.Backtrack:
      color = '#613080';
      break;
    case UserState.Overwrite:
      color = '#0000';
      break;
  }
  return color;
};

// Fetch positions size
const fetchPositionBufferSeconds: number = 10;
const fetchPositionFutureBuffers: number = 6;
const fetchPositionPastBuffers: number = 1;

// Update our last labeled frame every x seconds
const updateLLFAfterTime: number = 8;
// Update videoplayer every ms
const updateIntervalMs: number = 40;
// Playback speed during new position interval
const intervalPlaybackSpeed: number = 0.5;
// When adding new positions, do it every x seconds
const extendIntervalSeconds: number = 0.5;
// Total length of the proximity trail in seconds
const proximityTrailSeconds: number = 4;
// Allowed seconds without popup to appear in review state
const correctionNoPopupSeconds: number = 10;

const PlayerLabelTask = (props: Props) => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  
  const { session, task, runId, lineup, labelTasks, reloadTask } = props;
  const taskId: string = String(task.id);

  const fps: number = (session?.videos && session.videos.length > 0 && session.videos[0].fps) || 25;

  const fetchPositionBufferSize: number = saneRound(fps * fetchPositionBufferSeconds);

  const startFrame: number = task?.startFrame || 0;
  const startTime: number = startFrame / fps;
  const endFrame: number = task?.endFrame || 0;
  const endTime: number = endFrame / fps;

  const [cursor, setCursor] = useState<Cursor>(Cursor.Default);
  const [cursorPosition, setCursorPosition] = useState<CursorPosition | undefined>(undefined);
  const [lastRightClick, setLastRightClick] = useState<ClickAction | undefined>(undefined);

  const [videoPlaying, setVideoPlaying] = useState<boolean>(false);
  const [playTime, setPlayTime] = useState<number>(startTime);
  const [playbackSpeed, setPlaybackSpeed] = useState<number>(1);
  const [seekVideo, setSeekVideo] = useState<boolean>(false);
  const [playAfterSeek, setPlayAfterSeek] = useState<boolean>(false);
  const [timeStep, setTimeStep] = useState<number>(1);

  const [snapToTrack, setSnapToTrack] = useState<boolean>(true);
  const [showTrail, setShowTrail] = useState<boolean>(true);

  const [userState, setUserState] = useState<UserState>(UserState.Init);
  const [prevState, setPrevState] = useState<UserState | undefined>(undefined);
  const [subState, setSubState] = useState<SubState>(SubState.Entry);

  const [initLoad, setInitLoad] = useState<boolean>(true);
  const [positionBuffers, setPositionBuffers] = useState<PositionBuffer[]>([]);
  const [isFetchingPositions, setIsFetchingPositions] = useState<boolean>(false);
  const [isSavingData, setIsSavingData] = useState<boolean>(false);
  const [isUpdatingLLF, setIsUpdatingLLF] = useState<boolean>(false);
  const [lastLabeledFrameId, setLastLabeledFrameId] = useState<number>(-1);
  const [lastBufferFrameId, setLastBufferFrameId] = useState<number>(startFrame);
  const [newPositionTime, setNewPositionTime] = useState<number | undefined>(undefined);
  const [blockLoadingPositions, setBlockLoadingPositions] = useState<boolean>(false);
  const [connectionLost, setConnectionLost] = useState<boolean>(false);
  const [leftMouseIsDown, setLeftMouseIsDown] = useState<boolean>(false);

  const [storedClickData, setStoredClickData] = useState<ClickPositionData[]>([]);
  const [saveWhenReady, setSaveWhenReady] = useState<number>(-1);

  const [sideStartTime, setSideStartTime] = useState<number | undefined>(undefined);
  const [lastOverwriteFrameId, setLastOverwriteFrameId] = useState<number | undefined>(undefined);
  const [excludeRanges, setExcludeRanges] = useState<FrameRange[]>([]);
  const isOverwriting: boolean = userState === UserState.Overwrite;

  const [endBacktrackDialog, setEndBacktrackDialog] = useState<boolean>(false);
  const [updatePositionDialog, setUpdatePositionDialog] = useState<boolean>(false);

  const [skipTrackId, setSkipTrackId] = useState<string | undefined>(undefined);
  const [resetBuffersOnSave, setResetBuffersOnSave] = useState<boolean>(false);

  const [overridePanMethod, setOverridePanMethod] = useState<PanMethod | undefined>(undefined);
  const [legacyPlayerPositionsEnabled, setLegacyPlayerPositionsEnabled] = useLocalStorage<boolean>('legacyPlayerPositionsEnabled', false);
  const [baseLabelUrl, setBaseLabelUrl] = useState<string>('/gateway/label-assistant-v2/api/v1/label');
  

  useEffect(() => {
    setBaseLabelUrl(legacyPlayerPositionsEnabled ? '/gateway/label-assistant' : '/gateway/label-assistant-v2/api/v1/label');
  }, [legacyPlayerPositionsEnabled]);

  const { flags, setFlagStatus, removeFlags } = useValidationFlags(runId, taskId, baseLabelUrl);

  const handleLegacyPlayerPositionsToggle = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setLegacyPlayerPositionsEnabled(event.target.checked);
    },
    [setLegacyPlayerPositionsEnabled]
  );

  const currentFrameId: number = saneRound(playTime * fps);
  const lastLabeledFrameTime: number = lastLabeledFrameId / fps;
  const lastDataFrameTime: number = Math.min(endTime, Math.max(lastBufferFrameId / fps, startTime));
  
  const { 
    sidebarButtons: supervisorFunctions, 
    llfAsEndChoice, 
    deleteChoice,
  } = useSupervisorFunctions({
    session,
    task,
    lastLabeledFrameId,
    reloadTask,
  });

  const fetchFromBuffer: PositionBuffer | undefined = useMemo(
    () => positionBuffers.find((x) => x.page_start <= currentFrameId && x.page_start + x.page_size > currentFrameId),
    [currentFrameId, positionBuffers]
  );

  const clearRightClick = useCallback(() => {
    setLastRightClick(undefined);
  }, []);
  // Unset right click with escape key
  useHotkeys('escape', clearRightClick, { keydown: true, keyup: false }, [clearRightClick]);

  const userStateString = useCallback(
    (state: UserState) => {
      switch (state) {
        case UserState.Init:
          return 'Init';
        case UserState.Review:
          return 'Reviewing';
        case UserState.Verify:
          return 'Verifying positions';
        case UserState.PlayUntilNewPosition:
          return 'Play until new position';
        case UserState.SetNewPosition:
          return 'Setting new position';
        case UserState.AddingPositions:
          return 'Adding new position(s)';
        case UserState.Backtrack:
          const addTimeDiffString: string =
            ' (+' + (sideStartTime ? (playTime - (sideStartTime || 0)).toFixed(1).toString() : '0') + 's)';
          if (storedClickData.length === 0) {
            return 'Pick a time and place to start backtracking' + addTimeDiffString;
          } else {
            return 'Backtracking' + addTimeDiffString;
          }
        case UserState.Overwrite:
          return 'Overwriting positions';
        default:
          return 'Unknown state';
      }
    },
    [playTime, sideStartTime, storedClickData.length]
  );

  const changeUserState = useCallback(
    (newState: UserState) => {
      if (userState === newState) return;
      // Current is now previous
      setPrevState(userState);
      // Trigger exitting our prev state
      setSubState(SubState.Exit);
      // Set new current state
      setUserState(newState);
    },
    [userState]
  );

  const isAllowedToPausePlay = useCallback(
    (newPlayingState: boolean | undefined = undefined) => {
      return !((newPlayingState === undefined || newPlayingState === true) && userState === UserState.SetNewPosition);
    },
    [userState]
  );

  const changeVideoPlaying = useCallback(
    (newPlayingState: boolean, logReason: string = '', alwaysAllow: boolean = false) => {
      if (!alwaysAllow && !isAllowedToPausePlay(newPlayingState)) {
        return;
      }
      setVideoPlaying((prevState) => {
        if (prevState === newPlayingState) {
          return prevState;
        }
        return newPlayingState;
      });
    },
    [isAllowedToPausePlay]
  );

  const onSeek = useCallback(() => {
    if (seekVideo) return;
    setSeekVideo(true);
    setPlayAfterSeek(false);
    setBlockLoadingPositions(false);
  }, [seekVideo]);

  const absoluteSeek = useCallback(
    (newTime: number) => {
      setPlayTime((prev) => {
        // Clamp time to allowed values
        const clampedTime = Math.max(startTime, Math.min(newTime, endTime));
        onSeek();
        return clampedTime;
      });
    },
    [onSeek, startTime, endTime]
  );

  const relativeSeek = useCallback(
    (timeDiff: number) => {
      setPlayTime((prev) => {
        // Clamp time to allowed values
        const clampedTime = Math.max(startTime, Math.min(prev + timeDiff, endTime));
        onSeek();
        return clampedTime;
      });
    },
    [endTime, onSeek, startTime]
  );

  // Seek to flag start time
  const seekToFlag = useCallback(
    (flag: Flag) => {
      absoluteSeek(flag.start / fps);
    },
    [absoluteSeek, fps]
  );

  const cleanBuffers = useCallback(() => {
    setPositionBuffers([]);
  }, []);

  const projectPositionData = useCallback(
    (data: PositionBuffer) => {
      data.video_coordinates = new Map<string, Coordinate[]>();

      // Go through all videos
      session?.videos?.forEach((video: Video) => {
        // Get our calibration
        const calib = JSON.parse(video?.calibration || '');

        // Get our world-, and if necessary also our ground positions
        const worldPositions: number[][] = [];

        data?.value?.forEach((value: Coordinate) => {
          const pos: number[] = [value.x || 0, value.y || 0, 0];
          worldPositions.push(pos);
        });

        // Project Positions
        const projectedData = projection(calib, worldPositions);
        const projCoords: Coordinate[] = [];
        for (let i: number = 0; i < projectedData.length; ++i) {
          const coord = projectedData[i];
          projCoords.push({
            x: coord[0],
            y: coord[1],
          });
        }

        data?.video_coordinates?.set(String(video.id), projCoords);
      });
    },
    [session]
  );

  const processFetchedBuffer = useCallback(
    (frameId: number, buffer: PositionBuffer | undefined) => {
      if (!buffer) {
        console.log('[ERROR] Invalid data received when fetching positions.');
        setConnectionLost(true);
        setBlockLoadingPositions(true);
        setIsFetchingPositions(false);
        return;
      }

      buffer.page_start = frameId;

      if (!buffer.value || buffer.value.length === 0) {
        console.log('[BUFFER] Not saving buffer, no positions received.');
        setBlockLoadingPositions(true);
        setIsFetchingPositions(false);
        return;
      }

      // Reset skip track id when end of this buffer surpassed our last labeled frame id
      if (buffer.page_start + buffer.page_size > lastLabeledFrameId) {
        setSkipTrackId(undefined);
      }

      // Project positions
      if (buffer) {
        projectPositionData(buffer);
      }

      console.log('[POSITIONS]', frameId, buffer);

      setPositionBuffers((prev: PositionBuffer[]) => [...prev, buffer].sort((a, b) => a.page_start - b.page_start));
      setLastBufferFrameId(buffer.page_start + buffer.page_size - 1);

      setIsFetchingPositions(false);
      setInitLoad(false);
    },
    [lastLabeledFrameId, projectPositionData]
  );

  const fetchBuffer = useCallback(
    (frameId: number, position: Coordinate | undefined = undefined) => {
      setIsFetchingPositions(true);

      const hasSkipTrackId: boolean = skipTrackId !== undefined;

      fetchGet(
        `${baseLabelUrl}/run/${runId}/player/${taskId}/positions?frame_id=${frameId}` +
          `&page_size=${fetchPositionBufferSize}` +
          (position !== undefined ? `&x=${position?.x}&y=${position?.y}` : '') +
          (hasSkipTrackId ? `&exclude_track_id=${skipTrackId}` : '') +
          `&auto_search=${snapToTrack ? 'true' : 'false'}`
      ).then((buffer: PositionBuffer | undefined) => {
        processFetchedBuffer(frameId, buffer);
      });
    },
    [
      baseLabelUrl,
      fetchPositionBufferSize,
      skipTrackId,
      processFetchedBuffer,
      runId,
      snapToTrack,
      taskId,
    ]
  );

  const stripBufferUntilFrame = useCallback((frameId: number) => {
    setLastBufferFrameId(frameId);

    // Set our stripped positionbuffers
    setPositionBuffers((prev: PositionBuffer[]) => {
      const newPositionBuffers: PositionBuffer[] = [];
      prev.forEach((posBuffer: PositionBuffer) => {
        // If end buffer is still before frame, we add all.
        const endFrame: number = posBuffer.page_start + posBuffer.page_size;

        if (endFrame <= frameId) {
          newPositionBuffers.push(posBuffer);
          return;
        }
        // If start_frame is before frame & end buffer after, we only add up unto frame
        else if (posBuffer.page_start <= frameId && endFrame > frameId) {
          const numToRemove: number = frameId - endFrame + 1;
          const partOfBuffer: PositionBuffer = {
            page_start: posBuffer.page_start,
            page_size: posBuffer.page_size - numToRemove,
            value: posBuffer.value?.slice(0, numToRemove + 1),
            video_coordinates: new Map<string, Coordinate[]>(),
            debug: posBuffer.debug,
          };
          posBuffer.video_coordinates?.forEach((value: Coordinate[], key: string) => {
            partOfBuffer.video_coordinates?.set(key, value.slice(0, numToRemove + 1));
          });
          newPositionBuffers.push(partOfBuffer);
        }
      });
      return newPositionBuffers;
    });
  }, []);

  // State exit
  const onStateExit = useCallback((state: UserState) => {
    switch (state) {
      case UserState.Verify:
        break;
      case UserState.PlayUntilNewPosition:
        break;
      case UserState.SetNewPosition:
        setNewPositionTime(undefined);
        break;
      case UserState.AddingPositions:
        setOverridePanMethod(undefined);
        setNewPositionTime(undefined);
        break;
      case UserState.Review:
        break;
      case UserState.Backtrack:
        setOverridePanMethod(undefined);
        setSideStartTime(undefined);
        break;
      case UserState.Overwrite:
        setOverridePanMethod(undefined);
        setSideStartTime(undefined);
        setLastOverwriteFrameId(undefined);
        break;
    }
  }, []);

  const projectClickData = useCallback(
    (data: ClickPositionData) => {
      if (!data.position) {
        console.log('[PROJECT] Trying to project click data without 3d position');
        return undefined;
      }

      const vidCoords: Map<string, Coordinate> = new Map<string, Coordinate>();
      const worldPosition: number[][] = [[data.position.x, data.position.y, data.position?.z || 0]];
      // We have our 3d position, now project it to all views!
      session.videos?.forEach((v: Video) => {
        // Get our calibration
        const calib = JSON.parse(v?.calibration || '');
        // Project Positions
        const projectedData = projection(calib, worldPosition);
        const projectedCoord: Coordinate = {
          x: projectedData[0][0],
          y: projectedData[0][1],
        };
        vidCoords.set(String(v.id), projectedCoord);
      });
      data.video_coordinates = vidCoords;
      return data;
    },
    [session.videos]
  );

  const fillStoredClickData = useCallback(() => {
    storedClickData.sort((a, b) => a.frameId - b.frameId);

    let prev = storedClickData[0];
    const interpolatedPositions: ClickPositionData[] = [];
    storedClickData.forEach((click: ClickPositionData) => {
      if (!click.position || !prev.position || click.frameId <= prev.frameId + 1) {
        prev = click;
        return;
      }
      // We have a gap, lets create positions to fill it
      const gapSize: number = click.frameId - prev.frameId;
      const xStep: number = (click.position.x - prev.position.x) / gapSize;
      const yStep: number = (click.position.y - prev.position.y) / gapSize;
      for (let i: number = 1; i < gapSize; ++i) {
        let newCp: ClickPositionData | undefined = {
          id: storedClickData.length + interpolatedPositions.length,
          frameId: prev.frameId + i,
          position: {
            x: prev.position.x + xStep * i,
            y: prev.position.y + yStep * i,
          },
        };
        newCp = projectClickData(newCp);
        newCp && interpolatedPositions.push(newCp);
      }

      prev = click;
    });
    storedClickData.push(...interpolatedPositions);
    interpolatedPositions.length > 0 &&
      console.log('[FILL] Added interpolations:', interpolatedPositions, storedClickData);
  }, [projectClickData, storedClickData]);

  const reprojectClick = useCallback(
    (click: ClickPositionData) => {
      const video: Video | undefined | null = session?.videos?.find(
        (vid: Video) => String(vid.id) === click.viewCreated
      );
      if (!video || !click.viewCreated) {
        console.log('[REPROJECT] Invalid click ' + click);
        setStoredClickData((prev) => prev.filter((x) => click.id !== x.id));
        return;
      }

      const vidCoord: Coordinate | undefined = click.video_coordinates?.get(click.viewCreated);
      if (!vidCoord) {
        console.log('[REPROJECT] Invalid click coordinate ' + click);
        setStoredClickData((prev) => prev.filter((x) => click.id !== x.id));
        return;
      }

      const calib = JSON.parse(video?.calibration || '');

      //get the 3d position from clicked point
      fetchPost('/gateway/calibrations/reprojection', {
        calibration: calib,
        camera_coordinates: [[vidCoord.x, vidCoord.y]],
      }).then((data: number[][]) => {
        // Hard-set z value for players to 0
        const position: Coordinate = { x: data[0][0], y: data[0][1], z: 0 };
        click.position = position;
        projectClickData(click);
        // Add click to storedClickData
        setStoredClickData((clicks: ClickPositionData[]) => {
          const newData: ClickPositionData[] = [...clicks.filter((x) => x.frameId !== click?.frameId), click];
          return newData.sort((a, b) => a.frameId - b.frameId);
        });
      });
    },
    [session.videos, projectClickData]
  );

  const addClickData = useCallback(
    (frameId: number, viewId: string, coord: Coordinate) => {
      // Add our new click
      const vidCoords: Map<string, Coordinate> = new Map<string, Coordinate>();
      vidCoords.set(viewId, coord);

      const newClick: ClickPositionData = {
        id: Date.now(),
        frameId: frameId,
        viewCreated: viewId,
        video_coordinates: vidCoords,
      };

      reprojectClick(newClick);
    },
    [reprojectClick]
  );

  // Add a 3d click
  const add3DClickData = useCallback(
    (frameId: number, position: Coordinate) => {
      let newClick: ClickPositionData | undefined = {
        id: storedClickData.length,
        frameId: frameId,
        position: position,
      };
      newClick = projectClickData(newClick);
      setStoredClickData((prev) => (newClick ? [...prev, newClick] : prev));
    },
    [projectClickData, storedClickData.length]
  );

  // Save position(s)
  const savePositions = useCallback(() => {
    if (storedClickData.length === 0) {
      console.log('[WARNING] No click data stored when saving..');
      return;
    }
    setIsSavingData(true);

    const positionsToSave: Coordinate[] = storedClickData
      .map((click: ClickPositionData) => {
        return {
          frame_id: click.frameId,
          x: click.position?.x || 0,
          y: click.position?.y || 0,
          track_id: click.position?.track_id || undefined,
        };
      })
      .sort((a, b) => a.frame_id - b.frame_id)
      .filter((c) => c !== undefined);

    // Get the highest frame id available in our clicked positions list
    const newLLF: number = positionsToSave[positionsToSave.length - 1].frame_id || 0;
    const overwriteBeforeLLF: boolean = isOverwriting && lastLabeledFrameId > newLLF;

    fetchPost(`${baseLabelUrl}/run/${runId}/player/${taskId}/positions?clear_future=${isOverwriting ? 'false' : 'true'}`, positionsToSave)
      .then((data: PositionData[] | undefined) => {
        if (!data) {
          setConnectionLost(true);
          return;
        }
        // Log a warning when data was empty.. continue though.
        if (data.length === 0) {
          console.log('[WARNING] No data returned after adding position');
        }

        console.log('[Added positions]', newLLF, positionsToSave, 'Overwrite before LLF: ', overwriteBeforeLLF);

        // Set our last labeled frame id
        setLastLabeledFrameId(overwriteBeforeLLF ? lastLabeledFrameId : newLLF);
        setIsUpdatingLLF(false);

        // Clear stored click data
        setStoredClickData([]);

        if (resetBuffersOnSave) {
          cleanBuffers();
          changeUserState(UserState.PlayUntilNewPosition);
          setResetBuffersOnSave(false);
        }

        // We're done saving data
        setIsSavingData(false);
        setBlockLoadingPositions(false);
      })
      .catch((error) => {
        console.log('[ERROR] Adding positions:', error, positionsToSave);
        enqueueSnackbar("Error #66, failed to add positions", { variant: 'error', autoHideDuration: 3000 });
        setIsSavingData(false);
      });
  }, [
    enqueueSnackbar,
    storedClickData,
    isOverwriting,
    baseLabelUrl,
    lastLabeledFrameId,
    runId,
    taskId,
    resetBuffersOnSave,
    cleanBuffers,
    changeUserState,
  ]);

  // Fetch status of our task
  const fetchTaskStatus = useCallback(() => {
    fetchGet('/gateway/label-assistant-v2/api/v1/label/run/' + runId + '/player/' + taskId + '/status').then(
      (status: TaskStatus | undefined) => {
        if (!status) {
          console.log('[ERROR] Invalid Task Status returned.');
          setConnectionLost(true);
          return;
        }
        console.log('Current Task Status: ', status);

        // set last labeled frame
        setLastLabeledFrameId(status.last_frame_id);

        // is last labeled frame null? Add initial position
        if (!status.last_frame_id) {
          const initPosition: Coordinate = {
            x: task?.startX || 0,
            y: task?.startY || 0,
            z: task?.startZ || 0,
          };
          add3DClickData(startFrame, initPosition);
          setSaveWhenReady(1);
          setResetBuffersOnSave(true);
        }
      }
    );
  }, [runId, taskId, task, add3DClickData, startFrame]);

  const savePositionsToNow = useCallback(() => {
    if (isUpdatingLLF) return;

    setIsUpdatingLLF(true);

    // Add until frame id
    const addUntilFrameId: number = Math.max(Math.min(currentFrameId, endFrame), startFrame);

    const addData: ClickPositionData[] = [];
    // Now add all positions to to-be-saved data
    for (let f: number = lastLabeledFrameId + 1; f <= addUntilFrameId; ++f) {
      const fBuffer: PositionBuffer | undefined = positionBuffers.find(
        (x) => x.page_start <= f && x.page_start + x.page_size > f
      );
      if (!fBuffer) {
        continue;
      }

      const framePos: Coordinate = fBuffer.value[f - fBuffer.page_start];
      const newClick: ClickPositionData = {
        id: f,
        frameId: f,
        position: framePos,
      };
      addData.push(newClick);
    }
    // Set stored click data and prep it for saving
    setStoredClickData(addData);
    setSaveWhenReady(addData.length);

    console.log('[Update LLF] To be saved: ', lastLabeledFrameId, addUntilFrameId, addData);
  }, [currentFrameId, endFrame, isUpdatingLLF, lastLabeledFrameId, positionBuffers, startFrame]);

  const onChangeLineupEntry = useCallback(
    async (value: LineupEntry) => {
      const offsetSeconds: number = timeSpanToSeconds(session?.startAndFinishOffset) || 3;
      const periodStartFrame = (p: { startTimeInMilliseconds?: number | null }) =>
        saneRound(((p.startTimeInMilliseconds || 0) / 1000 - offsetSeconds) * fps);
      const periodEndFrame = (p: { endTimeInMilliseconds?: number | null }) =>
        saneRound(((p.endTimeInMilliseconds || 0) / 1000 + offsetSeconds) * fps);
      const update = async (endFrame: number) => {
        await labelTasksService.updateLabelTask(task.id, {
          ...labelTaskInput(task),
          endFrame: endFrame,
          classType: value.type || labelTaskInput(task).classType,
          lineupEntryId: value.id,
        });
        reloadTask();
      };

      if (value.outSeconds && value.outSeconds > 0) {
        //This player leaves the pitch at some point so we must adapt the end_frame of this label task
        //so that the labeler knows when to stop labelling
        let end_frame = 0;
        if (value.outEvent?.timestamp) {
          //This was a red card or injury so the event is linked to this lineup entry
          end_frame = saneRound(timeSpanToSeconds(value.outEvent.timestamp) * fps);
        }
        if (task && session && end_frame > 0) {
          //Check if this new end_frame is in the same period as what we are currently labeling
          const correctPeriod = session?.periods?.find(
            (p) =>
              task.startFrame >= periodStartFrame(p) &&
              task.startFrame <= periodEndFrame(p) &&
              end_frame >= periodStartFrame(p) &&
              end_frame <= periodEndFrame(p)
          );
          if (correctPeriod) {
            await update(end_frame);
            return;
          }
        }
      }
      if (task && session) {
        //This player does not leave the pitch, set the end_frame to the end of the period
        const correctPeriod = session?.periods?.find(
          (p) => task.startFrame >= periodStartFrame(p) && task.startFrame <= periodEndFrame(p)
        );
        if (correctPeriod) {
          await update(periodEndFrame(correctPeriod));
        }
      }
    },
    [session, task, fps, reloadTask]
  );

  const onChangePlaybackspeed = useCallback((value: number) => {
    setPlaybackSpeed(value);
  }, []);

  const updatePosition = useCallback(() => {
    // Remove dialog
    setUpdatePositionDialog(false);
    // Don't allow editing positions on start frame
    if (currentFrameId === startFrame) return;
    // Remove right click menu
    clearRightClick();
    // Save positions up until this frame.
    savePositionsToNow();
    // Go to new position state
    changeUserState(UserState.SetNewPosition);
    // Remove flags after this frame
    removeFlags(currentFrameId);
  }, [currentFrameId, startFrame, clearRightClick, savePositionsToNow, changeUserState, removeFlags]);

  const tryUpdatePosition = useCallback(() => {
    if (userState === UserState.Review && playTime < lastLabeledFrameTime - correctionNoPopupSeconds) {
      setUpdatePositionDialog(true);
    } else {
      updatePosition();
    }
  }, [lastLabeledFrameTime, playTime, updatePosition, userState]);

  /* BACKTRACK */
  const startBacktracking = useCallback(() => {
    clearRightClick();
    changeUserState(UserState.Backtrack);
  }, [changeUserState, clearRightClick]);

  /* OVERWRITE */
  const startOverwrite = useCallback(() => {
    clearRightClick();
    changeUserState(UserState.Overwrite);

    // Add current position to our overwrite position
    if (!fetchFromBuffer) return;

    add3DClickData(currentFrameId, fetchFromBuffer.value[currentFrameId - fetchFromBuffer.page_start]);
  }, [add3DClickData, changeUserState, clearRightClick, currentFrameId, fetchFromBuffer]);

  const cancelSideTrack = useCallback(() => {
    clearRightClick();

    // Clean data
    setStoredClickData([]);
    sideStartTime && absoluteSeek(sideStartTime);
    cleanBuffers();

    changeUserState(UserState.Review);
  }, [absoluteSeek, changeUserState, cleanBuffers, clearRightClick, sideStartTime]);

  const saveSideTrack = useCallback(() => {
    clearRightClick();
    setEndBacktrackDialog(false);

    sideStartTime && absoluteSeek(sideStartTime);

    // Save data when ready
    setSaveWhenReady(storedClickData.length);
    setResetBuffersOnSave(true);
  }, [absoluteSeek, clearRightClick, sideStartTime, storedClickData.length]);

  const onViewMouseDown = useCallback(
    (viewId: string, info: ClickInfo) => {
      if (info.button === MouseButton.LeftMouseButton) {
        setLeftMouseIsDown(true);

        // If our last right click is set, we use this click to unset it first
        if (lastRightClick) {
          clearRightClick();
          return;
        }

        switch (userState) {
          case UserState.SetNewPosition:
            changeUserState(UserState.AddingPositions);
            break;
          case UserState.Backtrack:
            addClickData(
              currentFrameId,
              cursorPosition?.viewId || viewId,
              cursorPosition?.coord || { x: info.x, y: info.y }
            );
            if (sideStartTime && playTime - extendIntervalSeconds > sideStartTime) {
              // We're still +interval above our start time
              relativeSeek(-extendIntervalSeconds);
            } else {
              // We reached our end
              setEndBacktrackDialog(true);
            }
            break;
          case UserState.Overwrite:
            addClickData(
              currentFrameId,
              cursorPosition?.viewId || viewId,
              cursorPosition?.coord || { x: info.x, y: info.y }
            );
            break;
        }
      } else if (info.button === MouseButton.RightMouseButton) {
        // Set our last right click
        setLastRightClick({
          viewId: viewId,
          info: info,
        });
      }
    },
    [
      lastRightClick,
      userState,
      clearRightClick,
      changeUserState,
      addClickData,
      currentFrameId,
      cursorPosition,
      sideStartTime,
      playTime,
      relativeSeek,
    ]
  );

  const onViewMouseUp = useCallback(
    (viewId: string, info: ClickInfo) => {
      if (info.button === MouseButton.LeftMouseButton && leftMouseIsDown) {
        setLeftMouseIsDown(false);

        switch (userState) {
          case UserState.AddingPositions:
            // Save our data when ready
            setSaveWhenReady(storedClickData.length + 1);
            // Add our release click data to trigger saving
            addClickData(
              currentFrameId,
              cursorPosition?.viewId || viewId,
              cursorPosition?.coord || { x: info.x, y: info.y }
            );

            // Reset on add
            setResetBuffersOnSave(true);
            // Set skip track id if we have one
            const currentCoordinate: Coordinate | undefined =
              fetchFromBuffer?.value[currentFrameId - fetchFromBuffer.page_start];
            if (currentCoordinate?.track_id) {
              setSkipTrackId(currentCoordinate?.track_id);
            }
            break;
        }
      }
    },
    [leftMouseIsDown, userState, storedClickData.length, addClickData, currentFrameId, cursorPosition, fetchFromBuffer]
  );

  const onViewMouseMove = useCallback((viewId: string, coord: Coordinate) => {
    setCursorPosition({
      viewId,
      coord,
    });
  }, []);

  // State entry
  const onStateEntry = useCallback(
    (state: UserState) => {
      //console.log("> ", userStateString(state));
      switch (state) {
        case UserState.Init:
          fetchTaskStatus();
          break;
        case UserState.Verify:
          setCursor(Cursor.Verify);
          break;
        case UserState.PlayUntilNewPosition:
          setCursor(Cursor.Waiting);
          break;
        case UserState.SetNewPosition:
          setCursor(Cursor.Add);

          const t: number = prevState === UserState.Verify ? lastDataFrameTime : lastLabeledFrameTime;
          const frameId: number = Math.min(currentFrameId, saneRound((t + extendIntervalSeconds) * fps));

          setNewPositionTime(frameId / fps);
          changeVideoPlaying(false, 'Entry State - Set New Position');
          stripBufferUntilFrame(frameId);
          break;
        case UserState.AddingPositions:
          setCursor(Cursor.Add);
          setOverridePanMethod(PanMethod.Edge);
          setNewPositionTime(playTime + extendIntervalSeconds);
          setPlayAfterSeek(true);
          break;
        case UserState.Review:
          setCursor(Cursor.Default);
          break;
        case UserState.Backtrack:
          setCursor(Cursor.Add);
          setOverridePanMethod(PanMethod.Edge);
          setSideStartTime(playTime);
          setPlayAfterSeek(true);
          break;
        case UserState.Overwrite:
          setCursor(Cursor.Add);
          setOverridePanMethod(PanMethod.Edge);
          setPlaybackSpeed(0.5);
          setSideStartTime(playTime);
          break;
      }
    },
    [
      fetchTaskStatus,
      prevState,
      lastDataFrameTime,
      lastLabeledFrameTime,
      currentFrameId,
      fps,
      changeVideoPlaying,
      stripBufferUntilFrame,
      playTime,
    ]
  );

  // Load future & past positions
  useEffect(() => {
    if (
      userState === UserState.Init ||
      userState === UserState.Backtrack ||
      isFetchingPositions ||
      isSavingData ||
      blockLoadingPositions ||
      connectionLost ||
      subState !== SubState.Active
    ) {
      return;
    }

    const bufferSize: number = positionBuffers.length;
    // Do we want more buffers?
    if (bufferSize < fetchPositionPastBuffers + fetchPositionFutureBuffers) {
      if (bufferSize === 0) {
        const fetchFrameId: number = currentFrameId - fetchPositionBufferSize * fetchPositionPastBuffers;
        fetchBuffer(Math.max(startFrame, fetchFrameId));
      } else {
        // Check if we fetch a buffer before our current
        const fetchBefore: boolean = fetchFromBuffer ? fetchFromBuffer.page_start > currentFrameId : false;
        if (fetchBefore) {
          const bufferAtStart: PositionBuffer = positionBuffers[0];
          const bufferFull: boolean = (bufferAtStart.value?.length || 0) === fetchPositionBufferSize;
          if (bufferFull) {
            // Load next buffer
            fetchBuffer(bufferAtStart.page_start - fetchPositionBufferSize);
          }
        } else {
          // Else we fetch one after
          const bufferAtEnd: PositionBuffer = positionBuffers[bufferSize - 1];
          const bufferFull: boolean = (bufferAtEnd.value?.length || 0) === fetchPositionBufferSize;
          if (bufferFull) {
            // Load next buffer
            const fetchFrameId: number = bufferAtEnd.page_start + fetchPositionBufferSize;
            // If the fetched frame id is larger than the last labeled frame, we need to do a positional search get positions instead.
            // Meaning we get the last position available in our available buffer, and use that to fetch future positions
            if (fetchFrameId > lastLabeledFrameId && snapToTrack) {
              const fetchPosition: Coordinate | undefined = bufferAtEnd.value[bufferAtEnd.page_size - 1];
              fetchBuffer(fetchFrameId, fetchPosition);
            } else {
              fetchBuffer(fetchFrameId);
            }
          }
        }
      }
    }
  }, [
    fetchPositionBufferSize,
    subState,
    connectionLost,
    isSavingData,
    currentFrameId,
    fetchFromBuffer,
    fetchBuffer,
    isFetchingPositions,
    positionBuffers,
    startFrame,
    userState,
    lastLabeledFrameId,
    snapToTrack,
    blockLoadingPositions,
  ]);

  // Handle position buffers
  useEffect(() => {
    if (
      positionBuffers.length === 0 ||
      userState === UserState.Init ||
      userState === UserState.SetNewPosition ||
      userState === UserState.PlayUntilNewPosition ||
      userState === UserState.AddingPositions ||
      subState !== SubState.Active
    ) {
      return;
    }

    const firstBuffer: PositionBuffer = positionBuffers[0];
    const lastBuffer: PositionBuffer = positionBuffers[positionBuffers.length - 1];

    if (currentFrameId < firstBuffer.page_start) {
      // Remove all buffers if we're before our first buffer's start
      console.log('Clean buffers - Frame id < first buffer start');
      cleanBuffers();
    } else if (positionBuffers.length > fetchPositionPastBuffers) {
      const currentBuffer: PositionBuffer = positionBuffers[fetchPositionPastBuffers];

      if (userState !== UserState.Verify && currentFrameId >= lastBuffer.page_start + lastBuffer.page_size) {
        // Remove all buffers if we're after our last buffer's end
        console.log('Clean buffers - Frame id > last buffer end');
        cleanBuffers();
      } else if (currentFrameId > currentBuffer.page_start + currentBuffer.page_size) {
        // Remove first buffer when we're surpassing our current buffer's end
        console.log('Remove first buffer');
        setPositionBuffers((prev: PositionBuffer[]) => {
          prev.shift();
          return [...prev];
        });
      } else if (
        currentFrameId > startFrame + fetchPositionPastBuffers * fetchPositionBufferSize &&
        currentFrameId < currentBuffer.page_start &&
        positionBuffers.length > fetchPositionPastBuffers + fetchPositionFutureBuffers
      ) {
        // Remove last buffer when we're surpassing our current buffer's start
        console.log('Remove last buffer', positionBuffers.length, currentFrameId, currentBuffer.page_start);
        setPositionBuffers((prev: PositionBuffer[]) => {
          prev.pop();
          return [...prev];
        });
      }
    }
  }, [
    fetchPositionBufferSize,
    subState,
    connectionLost,
    currentFrameId,
    isFetchingPositions,
    startFrame,
    positionBuffers,
    cleanBuffers,
    userState,
  ]);

  // Handle data saving
  useEffect(() => {
    if (saveWhenReady === storedClickData.length && !isSavingData) {
      const allPositionsReprojected: boolean = storedClickData.every(
        (click: ClickPositionData) => click.position !== undefined
      );
      if (allPositionsReprojected) {
        // Save all click data
        savePositions();
        // And we're done
        setSaveWhenReady(-1);
      }
    }
  }, [savePositions, saveWhenReady, storedClickData, isSavingData]);

  // Handle play after seek
  useEffect(() => {
    if (!seekVideo && playAfterSeek) {
      changeVideoPlaying(true, 'Play after seek!');
      setPlayAfterSeek(false);
    }
  }, [playAfterSeek, seekVideo, changeVideoPlaying]);

  // If flags change, we wipe our exclude ranges
  useEffect(() => {
    if(flags) {
      setExcludeRanges([]);
    }
  }, [flags]);

  // Set flag exclusion frame ranges when overwriting
  useEffect(() => {
    if(sideStartTime && lastOverwriteFrameId) {
      setExcludeRanges([{
        start_frame: saneRound(sideStartTime * fps),
        end_frame: lastOverwriteFrameId,
      }]);
    }
  }, [fps, lastOverwriteFrameId, sideStartTime]);

  // Sub state handling
  useEffect(() => {
    switch (subState) {
      case SubState.Exit:
        prevState && onStateExit(prevState);
        setSubState(SubState.Entry);
        break;
      case SubState.Entry:
        onStateEntry(userState);
        setSubState(SubState.Active);
        break;
      case SubState.Active:
        break;
    }
  }, [onStateEntry, onStateExit, prevState, subState, userState]);

  // While in Init state...
  useEffect(() => {
    if (userState !== UserState.Init || subState !== SubState.Active) return;

    if (lastLabeledFrameId !== -1) {
      // seek to last labeled frame id on init
      absoluteSeek(lastLabeledFrameId / fps);
      changeUserState(UserState.Review);
    }
  }, [changeUserState, fps, lastLabeledFrameId, absoluteSeek, userState, subState]);

  // While in Review state...
  useEffect(() => {
    if (userState !== UserState.Review || subState !== SubState.Active) return;
    if (seekVideo) return;

    if (playTime >= endTime) return;

    // Handle state changing
    if (playTime > lastLabeledFrameTime) {
      changeUserState(UserState.Verify);
      return;
    } else if (playTime > lastDataFrameTime && playTime > lastLabeledFrameTime) {
      changeUserState(UserState.PlayUntilNewPosition);
      return;
    }
  }, [changeUserState, endTime, lastDataFrameTime, lastLabeledFrameTime, playTime, seekVideo, subState, userState]);

  // While in Verify state...
  useEffect(() => {
    if (userState !== UserState.Verify || subState !== SubState.Active) return;
    if (seekVideo) return;

    // Did snap to track get disabled, trigger updatePosition
    if (!snapToTrack) {
      updatePosition();
      return;
    }

    // Handle state changing
    if (playTime <= lastLabeledFrameTime) {
      changeUserState(UserState.Review);
      return;
    } else if (playTime > lastDataFrameTime) {
      savePositionsToNow();
      changeUserState(UserState.PlayUntilNewPosition);
      return;
    }

    // Update LLF when we surpass our last labeled frame time
    if (playTime >= lastLabeledFrameTime + updateLLFAfterTime) {
      savePositionsToNow();
    }

    // Don't continue if we don't have the buffer to retrieve our position from
    if (!fetchFromBuffer) {
      return;
    }

    // Handle end of label task
    const positionIndex: number = currentFrameId - fetchFromBuffer.page_start;
    const dataAvailable: boolean = positionIndex >= 0 && positionIndex < (fetchFromBuffer.value?.length || 0);
    if (currentFrameId === endFrame && lastLabeledFrameTime < endTime) {
      if (dataAvailable) {
        savePositionsToNow();
      } else {
        changeUserState(UserState.SetNewPosition);
      }
    }
  }, [
    changeUserState,
    currentFrameId,
    endFrame,
    endTime,
    fetchFromBuffer,
    lastDataFrameTime,
    lastLabeledFrameTime,
    playTime,
    savePositionsToNow,
    seekVideo,
    snapToTrack,
    subState,
    updatePosition,
    userState,
  ]);

  // While in PlayUntilNewPosition state...
  useEffect(() => {
    if (userState !== UserState.PlayUntilNewPosition || subState !== SubState.Active) return;
    if (seekVideo || isSavingData) return;

    if (playTime >= Math.min(endTime, lastDataFrameTime + extendIntervalSeconds)) {
      changeUserState(UserState.SetNewPosition);
    } else if (playTime < lastDataFrameTime) {
      changeUserState(UserState.Review);
    }
  }, [
    changeUserState,
    endTime,
    isSavingData,
    lastDataFrameTime,
    lastLabeledFrameTime,
    playTime,
    seekVideo,
    subState,
    userState,
    videoPlaying,
  ]);

  // While in SetNewPosition state...
  useEffect(() => {
    if (userState !== UserState.SetNewPosition || subState !== SubState.Active) return;
    if (!newPositionTime || isFetchingPositions) return;
    if (seekVideo) return;

    const newPositionFrameId = saneRound(newPositionTime * fps);

    if (currentFrameId >= endFrame && lastLabeledFrameId >= endFrame) {
      changeUserState(UserState.Review);
    } else if (lastBufferFrameId > newPositionFrameId) {
      // When we have data after our new position time, we go to Verify (and play video)
      changeVideoPlaying(true, 'New position to verify', true);
      changeUserState(UserState.Verify);
    } else if (currentFrameId > newPositionFrameId) {
      // When playtime is bigger than new position time, we seek to our new position time
      absoluteSeek(newPositionTime);
    } else if (currentFrameId < newPositionFrameId) {
      // When playtime is smaller than our new position time, we go back to an earlier state (either review or play until new)
      if (currentFrameId < lastLabeledFrameId) {
        changeUserState(UserState.Review);
      } else {
        changeUserState(UserState.PlayUntilNewPosition);
      }
    }
  }, [
    absoluteSeek,
    changeUserState,
    currentFrameId,
    endFrame,
    fps,
    isFetchingPositions,
    lastBufferFrameId,
    lastLabeledFrameId,
    newPositionTime,
    changeVideoPlaying,
    subState,
    userState,
    isSavingData,
    isUpdatingLLF,
    seekVideo,
  ]);

  // While in Adding Positions state...
  useEffect(() => {
    if (userState !== UserState.AddingPositions || subState !== SubState.Active) return;
    if (isSavingData || isUpdatingLLF || seekVideo) return;

    if (newPositionTime && cursorPosition && leftMouseIsDown) {
      if (playTime >= newPositionTime) {
        addClickData(currentFrameId, cursorPosition.viewId, cursorPosition.coord);
        setNewPositionTime(newPositionTime + extendIntervalSeconds);
      }
    }
  }, [
    addClickData,
    currentFrameId,
    cursorPosition,
    isSavingData,
    isUpdatingLLF,
    leftMouseIsDown,
    newPositionTime,
    playTime,
    seekVideo,
    subState,
    userState,
  ]);

  // While in Backtrack state...
  useEffect(() => {
    if (userState !== UserState.Backtrack || subState !== SubState.Active) return;
  }, [subState, userState]);

  // While in Overwrite state...
  useEffect(() => {
    if (userState !== UserState.Overwrite || subState !== SubState.Active) return;

    //TEMP; try fill every time
    fillStoredClickData();

    // We have new stored overwrite data
    if (storedClickData.find((x) => !lastOverwriteFrameId || x.frameId > lastOverwriteFrameId) !== undefined) {
      const allPositionsReprojected: boolean = storedClickData.every(
        (click: ClickPositionData) => click.position !== undefined
      );

      if (allPositionsReprojected) {
        const lastPos: ClickPositionData | undefined = storedClickData[storedClickData.length - 1];
        setLastOverwriteFrameId(lastPos.frameId);
      }
    }
  }, [currentFrameId, fetchBuffer, fillStoredClickData, lastOverwriteFrameId, storedClickData, subState, userState]);

  /*
    CREATE VIDEO OVERLAY ITEMS
  */
  const overlayItems: Map<string, VideoOverlayObject[]> = new Map<string, VideoOverlayObject[]>();

  session?.videos?.forEach((video: Video) => {
    const viewOverlayObjects: VideoOverlayObject[] = overlayItems.get(String(video.id)) || [];
    const viewClickData: ClickPositionData[] = storedClickData.filter(
      (click: ClickPositionData) => click.video_coordinates?.get(String(video.id)) !== undefined
    );

    let coord: Coordinate | undefined = undefined;

    if (fetchFromBuffer) {
      // When our current frame id is available in any buffer..
      const positionIndex: number = currentFrameId - fetchFromBuffer.page_start;
      const vidCoords: Coordinate[] = fetchFromBuffer.video_coordinates?.get(String(video.id)) || [];
      coord =
        viewClickData.length > 0 && !isOverwriting
          ? viewClickData[userState === UserState.Backtrack ? 0 : viewClickData.length - 1].video_coordinates?.get(
              String(video.id)
            )
          : positionIndex >= 0
          ? vidCoords[Math.min(positionIndex, vidCoords.length - 1)]
          : undefined;
    } else if (positionBuffers.length > 0) {
      // When our current frame id isn't in any existing buffer..
      const vidCoords: Coordinate[] =
        positionBuffers[positionBuffers.length - 1].video_coordinates?.get(String(video.id)) || [];
      coord =
        viewClickData.length > 0
          ? viewClickData[userState === UserState.Backtrack ? 0 : viewClickData.length - 1].video_coordinates?.get(
              String(video.id)
            )
          : vidCoords[vidCoords.length - 1];
    }

    // Initialize showCoords list
    const trailCoords: Coordinate[] = [];
    const sideTrailCoords: Coordinate[] = [];

    if (showTrail) {
      // Then add all buffered positions
      positionBuffers.forEach((buffer: PositionBuffer) => {
        trailCoords.push(...(buffer.video_coordinates?.get(String(video.id)) || []));
      });
      // And also our stored click data from this view
      viewClickData.forEach((click: ClickPositionData) => {
        const vc: Coordinate | undefined = click.video_coordinates?.get(String(video.id));
        if (vc) {
          // When overwriting, we add this to our side trail instead
          if (isOverwriting) {
            sideTrailCoords.push(vc);
          } else {
            trailCoords.push(vc);
          }
        }
      });
      // Create trail overlay object
      const trail: VideoOverlayObject | undefined = Trail({
        currentCoord: coord,
        trailCoords: trailCoords,
        proximityLength: !isOverwriting ? proximityTrailSeconds * 0.5 * fps : 0,
        maxX: video.width || undefined,
        maxY: video.height || undefined,
        primaryColor: '#222',
        secondaryColor: '#ddd',
      });
      // If we have a trail, add it
      trail && viewOverlayObjects.push(trail);
    }

    if (coord) {
      // Create player marker overlay object
      const playerObjects: VideoOverlayObject[] = PlayerMarker({
        coord: coord,
        color: getUserStateColor(userState),
        showAsX: isAddPositionState(userState),
        centerViewOnThis: !isOverwriting,
      });

      viewOverlayObjects.push(...playerObjects);
    }

    // Get our current side position, if not found use the last one available.
    const sidePosition: Coordinate | undefined = (
      viewClickData.find((x) => x.frameId === currentFrameId) || viewClickData[viewClickData.length - 1]
    )?.video_coordinates?.get(String(video.id));

    // Create our side trail
    const sideTrail: VideoOverlayObject | undefined = Trail({
      currentCoord: sidePosition,
      trailCoords: sideTrailCoords,
      proximityLength: 0,
      maxX: video.width || undefined,
      maxY: video.height || undefined,
      primaryColor: '#00ff95',
      secondaryColor: '#318600',
    });
    sideTrail && viewOverlayObjects.push(sideTrail);

    // Add marker on sideposition
    if (sidePosition) {
      const sideObjects: VideoOverlayObject[] = PlayerMarker({
        coord: sidePosition,
        color: '#00ff95',
        showAsX: true,
        centerViewOnThis: isOverwriting,
      });
      viewOverlayObjects.push(...sideObjects);
    }

    // Validation flags

    // Flag trails
    const flagTrails: VideoOverlayObject | undefined = FlagTrails({
      flags: flags,
      buffers: positionBuffers,
      excludeRanges: excludeRanges,
      video: video,
      primaryColor: '#900',
      secondaryColor: '#aaa',
    });
    flagTrails && viewOverlayObjects.push(flagTrails);

    // Add flag icons @ start position
    const flagMarkers: VideoOverlayObject[] = FlagMarkers({
      flags: flags,
      buffers: positionBuffers,
      excludeRanges: excludeRanges,
      video: video,
      setFlagStatus: setFlagStatus,
      seekToFlag: seekToFlag,
      disableSelection: userState === UserState.AddingPositions || userState === UserState.Overwrite || userState === UserState.Backtrack
    });
    viewOverlayObjects.push(...flagMarkers);

    // Final step: set our view overlay objects
    overlayItems.set(String(video.id), viewOverlayObjects);
  });

  // Mouse Overlay
  const mouseOverlay: VideoOverlayObject[] | undefined = useMemo(
    () =>
      userState === UserState.SetNewPosition ||
      userState === UserState.AddingPositions ||
      userState === UserState.Backtrack ||
      userState === UserState.Overwrite
        ? PlayerMarker({
            coord: { x: 0, y: 0 },
            color: '#48c948',
          })
        : undefined,
    [userState]
  );

  /* 
    END VIDEO OVERLAY ITEMS 
  */

  const debugText: string[] = useMemo(
    () => [
      'State: ' + userStateString(userState),
      'Frame: ' + currentFrameId,
      'LLF: ' + lastLabeledFrameId,
      'LDF: ' + saneRound(lastDataFrameTime * fps),
      'NPF: ' + (newPositionTime ? saneRound((newPositionTime || 0) * fps).toString() : 'undefined'),
      'LBF: ' + lastBufferFrameId,
      'LOF: ' + lastOverwriteFrameId,
      'Play: ' + videoPlaying,
      'isSeeking: ' + seekVideo,
      'track id: ' + skipTrackId,
      'LMB pressed: ' + leftMouseIsDown,
      'updating LLF: ' + isUpdatingLLF,
      'saving Data: ' + isSavingData,
      'fetching positions: ' + isFetchingPositions,
      'FPS: ' + fps,
      'substate: ' + subState,
    ],
    [
      currentFrameId,
      fps,
      isFetchingPositions,
      isSavingData,
      isUpdatingLLF,
      lastBufferFrameId,
      lastDataFrameTime,
      lastLabeledFrameId,
      lastOverwriteFrameId,
      leftMouseIsDown,
      newPositionTime,
      seekVideo,
      skipTrackId,
      subState,
      userState,
      userStateString,
      videoPlaying,
    ]
  );

  const componentPlaybackspeed: number = useMemo(
    () => (isAddPositionState(userState) ? intervalPlaybackSpeed : playbackSpeed),
    [playbackSpeed, userState]
  );

  const videoOverlayTexts: TextOverlay[] = useMemo(
    () => [
      ...(process.env.REACT_APP_SHOW_DEBUG === 'true' ? fetchFromBuffer?.debug || [] : []),
      { value: userStateString(userState) },
    ],
    [fetchFromBuffer, userState, userStateString]
  );

  const viewMenuItems: JSX.Element[] = useMemo(() => {
    return [
      <ToggleMenuItem
        key="showtrail"
        value={showTrail}
        title="Trail"
        shortcut="Shift+T"
        open={lastRightClick !== undefined}
        onChange={(value: boolean) => setShowTrail(value)}
      />,
    ];
  }, [lastRightClick, showTrail]);

  const editMenuItems: JSX.Element[] = useMemo(() => {
    return [
      <ToggleMenuItem
        key="snaptotrack"
        value={snapToTrack}
        title="Magnet"
        shortcut="Shift+M"
        open={lastRightClick !== undefined}
        onChange={(value: boolean) => setSnapToTrack(value)}
      />,
      <ActionableMenuItem
        key="updatePosition"
        icon={<BackspaceIcon />}
        title="Update"
        shortcut="Shift+U"
        open={lastRightClick !== undefined}
        disabled={userState !== UserState.Review && userState !== UserState.Verify}
        onTrigger={tryUpdatePosition}
      />,
      <ActionableMenuItem
        key="overwrite"
        icon={<OverwriteIcon />}
        title="Overwrite"
        shortcut="Shift+O"
        disabled={!(userState === UserState.Review || userState === UserState.Overwrite)}
        open={lastRightClick !== undefined}
        onTrigger={startOverwrite}
      />,
      <ActionableMenuItem
        key="backtrack"
        icon={<HistoryIcon />}
        title="Backtrack"
        shortcut="Shift+B"
        open={lastRightClick !== undefined}
        disabled={userState !== UserState.SetNewPosition}
        onTrigger={startBacktracking}
      />,
    ];
  }, [snapToTrack, lastRightClick, userState, startOverwrite, startBacktracking, tryUpdatePosition]);

  const editMenu: JSX.Element = useMemo(() => {
    return <Menu key="edit" title="Edit" items={editMenuItems} />;
  }, [editMenuItems]);

  const saveCancelMenuItems: JSX.Element[] = useMemo(() => {
    return [
      <ActionableMenuItem
        key="cancel"
        icon={<HistoryIcon />}
        title="Cancel"
        shortcut=""
        open={lastRightClick !== undefined}
        onTrigger={cancelSideTrack}
      />,
      <ActionableMenuItem
        key="save"
        icon={<BackspaceIcon />}
        title="Save"
        shortcut=""
        open={lastRightClick !== undefined}
        disabled={storedClickData.length === 0}
        onTrigger={saveSideTrack}
      />,
    ];
  }, [lastRightClick, saveSideTrack, cancelSideTrack, storedClickData]);

  const progressMenu: JSX.Element = useMemo(() => {
    return <Menu key="saveCancel" title="Progress" items={saveCancelMenuItems} />;
  }, [saveCancelMenuItems]);

  const closeEndBacktrackDialog = useCallback(() => {
    setEndBacktrackDialog(false);
  }, []);

  const closeUpdatePositionDialog = useCallback(() => {
    setUpdatePositionDialog(false);
  }, []);

  // Set our menu
  const menus: JSX.Element[] = [editMenu];
  const hasProgressMenu: boolean = userState === UserState.Backtrack || userState === UserState.Overwrite;
  if (hasProgressMenu) {
    menus.push(progressMenu);
  }

  // Choice dialogs
  const backtrackEndChoice: JSX.Element = (
    <AreYouSure
      open={endBacktrackDialog}
      title="Save Backtrack?"
      text="We're back at where we started. Do you want to save and continue?"
      onNo={closeEndBacktrackDialog}
      onYes={saveSideTrack}
    />
  );

  const updatePositionChoice: JSX.Element = (
    <AreYouSure
      open={updatePositionDialog}
      title="Clear all future positions?"
      text="Updating the position here will CLEAR future positions. Continue? (you may want to use Overwrite instead)"
      onNo={closeUpdatePositionDialog}
      onYes={updatePosition}
    />
  );

  const connectionLostPopup: JSX.Element = (
    <AreYouSure
      open={connectionLost}
      title="Connection lost."
      text="Lost connection to the server. Check if you're still connected to the internet. Refresh the page?"
      onNo={() => {}}
      onYes={() => window.location.reload(false)}
    />
  );

  const initLoadPopup: JSX.Element = initLoad ? (
    <div className={classes.overlay}>
      <div className={classes.popup}>
        <h1>Initializing...</h1>
        <p>We are initializing the services for labeling.<br />This may take a while. Please be patient.</p>
      </div>
    </div>
  ) : <></>;

  return (
    <React.Fragment>
      {backtrackEndChoice}
      {updatePositionChoice}
      {llfAsEndChoice}
      {deleteChoice}
      {connectionLostPopup}
      {initLoadPopup}

      <div className={classes.flexContainer} style={{ cursor: cursor }}>
        {lastRightClick && (
          <div
            style={{
              position: 'absolute',
              left: lastRightClick.info.pageX,
              top: lastRightClick.info.pageY,
              zIndex: 1000,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'left',
            }}
          >
            {hasProgressMenu ? saveCancelMenuItems : editMenuItems}
          </div>
        )}

        <div className={classes.sidebar}>
          <div className={classes.sidebarRow}>
            <GoBackButton path={'/session/label/' + session.id} />
            <LabelCircularProgress task={task} lastLabeledFrameId={lastLabeledFrameId} />
            <LabelHelpButton />
            <LabelDebugInfoButton debugInfo={debugText} />
            { supervisorFunctions }
            <LegacyLabelAssistantToggleButton
              checked={legacyPlayerPositionsEnabled}
              className={classes.legacyToggleButton}
              name="legacyPlayerPositionsToggle"
              taskType="player positions"
              onChange={handleLegacyPlayerPositionsToggle}
            />
          </div>
          <LabelPlayerSidebar
            session={session}
            task={task}
            playTime={playTime}
            lineup={lineup}
            labelTasks={labelTasks}
            reloadTask={reloadTask}
            onChangeLineupEntry={onChangeLineupEntry}
          />
        </div>

        <div className={classes.videoContainer}>
          <VideoLayout
            videos={session?.videos || []}
            showOverlayUI={true}
            saveId="player-labeling"
            updateIntervalMs={updateIntervalMs}
            videoPlaying={videoPlaying}
            onChangeVideoPlaying={changeVideoPlaying}
            playTime={playTime}
            playTimeChanged={setPlayTime}
            seekVideo={seekVideo}
            setSeekVideo={setSeekVideo}
            playbackSpeed={componentPlaybackspeed}
            minPlayTime={startTime}
            maxPlayTime={endTime}
            viewOverlays={overlayItems}
            mouseOverlay={mouseOverlay || []}
            onViewMouseDown={onViewMouseDown}
            onViewMouseUp={onViewMouseUp}
            onViewMouseMove={onViewMouseMove}
            textOverlays={videoOverlayTexts}
            menus={menus}
            viewItems={viewMenuItems}
            defaultPanMethod={PanMethod.Center}
            overridePanMethod={overridePanMethod}
          />
        </div>
      </div>
      <div className={classes.flexContainer}>
        <div className={classes.videoControlsContainer}>
          <VideoControls
            isPlaying={videoPlaying}
            onChangeVideoPlaying={changeVideoPlaying}
            relativeSeek={relativeSeek}
            playbackSpeed={componentPlaybackspeed}
            setPlaybackSpeed={onChangePlaybackspeed}
            timeStep={timeStep}
            setTimeStep={setTimeStep}
            disablePausePlay={!isAllowedToPausePlay()}
          />
        </div>
        <div className={classes.timelineContainer}>
          <SessionTimeline
            playTime={playTime}
            fps={fps}
            seekToPlayTime={absoluteSeek}
            videoDuration={endTime - startTime}
            startTimeOffset={startTime}
            lastLabeledTime={lastLabeledFrameTime}
            lastProgressionTime={lastBufferFrameId / fps}
            flags={flags}
            slidingWindow
          />
        </div>
      </div>
    </React.Fragment>
  );
};

export default PlayerLabelTask;
